import React, { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Gift, Save, Info } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { active: false, instructions: "", submission_rules: "", attribution_rules: "",
  milestones_note: "", messaging_note: "" };

export default function ReferralPartners() {
  const { isAdmin, isGroupAdmin, isSuper } = useAuth();
  const canEdit = isAdmin || isGroupAdmin || isSuper;
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    api.get("/referral-program")
      .then((r) => setForm({ ...EMPTY, ...(r.data || {}) }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try { await api.put("/referral-program", form); toast.success("Referral program settings saved"); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-slate-400">Loading…</p>;

  return (
    <div className="max-w-2xl space-y-6" data-testid="referral-partners">
      <div className="flex items-center gap-3">
        <span className="rounded-lg bg-[#006090]/10 text-[#006090] p-2"><Gift className="w-5 h-5" /></span>
        <div>
          <h1 className="font-display text-xl font-bold text-slate-900">Referral Partner Program</h1>
          <p className="text-sm text-slate-500">Configure the referral framework. Payment execution is not enabled yet.{!canEdit ? " (Read-only — managers cannot edit.)" : ""}</p>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-5">
        <label className="flex items-center justify-between gap-4">
          <div>
            <p className="font-semibold text-slate-800 text-sm">Program Active</p>
            <p className="text-xs text-slate-500">Enable the referral partner program for this account.</p>
          </div>
          <Switch checked={!!form.active} disabled={!canEdit} onCheckedChange={(v) => setForm((f) => ({ ...f, active: v }))} data-testid="referral-active" />
        </label>

        <div>
          <Label className="text-xs font-semibold text-slate-500">Partner Instructions</Label>
          <Textarea value={form.instructions} disabled={!canEdit} onChange={set("instructions")} className="mt-1" rows={3}
            placeholder="What referral partners should know before submitting." data-testid="referral-instructions" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Referral Submission Rules</Label>
          <Textarea value={form.submission_rules} disabled={!canEdit} onChange={set("submission_rules")} className="mt-1" rows={3}
            placeholder="How referrals may be submitted and what is required." data-testid="referral-submission-rules" />
        </div>
        <div>
          <Label className="text-xs font-semibold text-slate-500">Duplicate / Attribution Rules</Label>
          <Textarea value={form.attribution_rules} disabled={!canEdit} onChange={set("attribution_rules")} className="mt-1" rows={3}
            placeholder="How duplicate referrals and attribution conflicts are resolved." data-testid="referral-attribution-rules" />
        </div>

        <div className="grid sm:grid-cols-2 gap-4 opacity-90">
          <div>
            <Label className="text-xs font-semibold text-slate-500">Milestone Definitions (placeholder)</Label>
            <Input value={form.milestones_note} disabled={!canEdit} onChange={set("milestones_note")} className="mt-1"
              placeholder="e.g. Submitted → Interviewed → Hired" data-testid="referral-milestones" />
          </div>
          <div>
            <Label className="text-xs font-semibold text-slate-500">Messaging Templates (placeholder)</Label>
            <Input value={form.messaging_note} disabled={!canEdit} onChange={set("messaging_note")} className="mt-1"
              placeholder="Link templates in Communications" data-testid="referral-messaging" />
          </div>
        </div>

        <p className="text-xs text-slate-400 inline-flex items-start gap-1.5">
          <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          This framework is expandable for the full Referral Partner program later (milestones, payouts, messaging).
        </p>

        {canEdit && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={saving} className="bg-[#006090] hover:bg-[#004b73]" data-testid="referral-save">
              <Save className="w-4 h-4 mr-1.5" /> Save
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
